
import { useEffect, useState, useRef } from 'react';

export default function TheMath() {
  const [animated, setAnimated] = useState(false);
  const [countUp, setCountUp] = useState({ founder: 0, preLaunch: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (animated) {
      const duration = 2000;
      const steps = 60;
      const founderTarget = 1488;
      const preLaunchTarget = 840;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        setCountUp({
          founder: Math.floor(founderTarget * progress),
          preLaunch: Math.floor(preLaunchTarget * progress),
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCountUp({ founder: founderTarget, preLaunch: preLaunchTarget });
        }
      }, stepTime);

      return () => clearInterval(interval);
    }
  }, [animated]);

  const tiers = [
    {
      when: 'Founder (now)',
      monthly: '$25',
      annual: '$300',
      savings: countUp.founder,
      discount: '83% off',
      highlight: true,
    },
    {
      when: 'Pre-Launch',
      monthly: '$79',
      annual: '$948',
      savings: countUp.preLaunch,
      discount: '47% off',
      highlight: false,
    },
    {
      when: 'Standard (Public Launch)',
      monthly: '$149',
      annual: '$1,788',
      savings: 0,
      discount: '$0',
      highlight: false,
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 lg:py-32 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2
          className={`font-harvey-serif text-[#09090b] text-[28px] sm:text-[38px] lg:text-[48px] font-normal tracking-tight text-center mb-8 sm:mb-12 lg:mb-16 transition-all duration-700 ${
            animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          The Math
        </h2>

        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-4">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`p-4 rounded-xl transition-all duration-700 ${
                tier.highlight
                  ? 'bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-2 border-[#d4af37]'
                  : 'bg-[#f5f0e8] border border-[#e8dcc8]'
              } ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={`font-harvey-sans text-[16px] font-medium tracking-wide mb-3 ${tier.highlight ? 'text-[#8B4513]' : 'text-[#09090b]'}`}>
                {tier.when}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="font-harvey-sans text-[#71717a] text-[10px] font-light tracking-wide mb-1">Monthly</div>
                  <div className="font-harvey-sans text-[#09090b] text-[14px] font-normal tracking-wide">{tier.monthly}</div>
                </div>
                <div>
                  <div className="font-harvey-sans text-[#71717a] text-[10px] font-light tracking-wide mb-1">Annual</div>
                  <div className="font-harvey-sans text-[#09090b] text-[14px] font-normal tracking-wide">{tier.annual}</div>
                </div>
                <div>
                  <div className="font-harvey-sans text-[#71717a] text-[10px] font-light tracking-wide mb-1">Savings</div>
                  <div className={`font-harvey-sans text-[14px] font-medium tracking-wide ${tier.highlight ? 'text-[#8B4513]' : 'text-[#8B4513]'}`}>
                    {tier.savings > 0 ? `$${tier.savings.toLocaleString()}` : tier.discount}
                    {tier.savings > 0 && <span className="block text-[9px]">({tier.discount})</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="font-harvey-sans text-[#71717a] text-sm lg:text-base font-medium tracking-wide px-4 py-3">When You Join</div>
            <div className="font-harvey-sans text-[#71717a] text-sm lg:text-base font-medium tracking-wide px-4 py-3">Monthly</div>
            <div className="font-harvey-sans text-[#71717a] text-sm lg:text-base font-medium tracking-wide px-4 py-3">Annual</div>
            <div className="font-harvey-sans text-[#71717a] text-sm lg:text-base font-medium tracking-wide px-4 py-3">Savings vs Standard</div>
          </div>

          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`grid grid-cols-4 gap-4 mb-4 transition-all duration-700 ${
                tier.highlight
                  ? 'bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-2 border-[#d4af37] rounded-xl'
                  : 'bg-[#f5f0e8] border border-[#e8dcc8] rounded-xl'
              } ${animated ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={`font-harvey-sans px-4 py-4 ${tier.highlight ? 'text-[#8B4513]' : 'text-[#09090b]'} text-sm lg:text-base font-medium tracking-wide`}>
                {tier.when}
              </div>
              <div className="font-harvey-sans text-[#09090b] px-4 py-4 text-sm lg:text-base font-normal tracking-wide">{tier.monthly}</div>
              <div className="font-harvey-sans text-[#09090b] px-4 py-4 text-sm lg:text-base font-normal tracking-wide">{tier.annual}</div>
              <div className={`font-harvey-sans px-4 py-4 ${tier.highlight ? 'text-[#8B4513]' : 'text-[#8B4513]'} text-sm lg:text-base font-medium tracking-wide`}>
                {tier.savings > 0 ? `$${tier.savings.toLocaleString()}/year` : tier.discount}
                {tier.savings > 0 && <span className="block text-xs mt-1">({tier.discount})</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
